import React, { useEffect, useRef, Suspense, lazy } from 'react';
import {
  CheckCircle,
  Shield,
  Clock,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Globe,
  Smartphone
} from 'lucide-react';
import { animate } from 'animejs';
import { ScrollReveal } from '../components/ScrollReveal';
import HomeAdOffer from '../components/HomeAdOffer';
import { useHeroEntrance } from '../hooks/useHeroEntrance';
import { useStaggerReveal } from '../hooks/useStaggerReveal';
import { prefersReducedMotion } from '../utils/motion';

// Lazy-loaded so the heavy 3D bundle doesn't block first paint.
const HeroTransaction3D = lazy(() => import('../components/HeroTransaction3D'));

const partnerLogos = [
  '/logos/1AD0AB1A-6F98-4ABE-A972-EFC01B990E36_4_5005_c.jpeg',
  '/logos/2CD2C90B-4B51-4753-B526-DDD8BBD84209_4_5005_c.jpeg',
  '/logos/3F60DBC9-73E6-425C-85DC-07547150C53F.jpeg',
  '/logos/4C06E6EB-0F82-4FD4-B4B2-FA2A6D1B4733.PNG',
  '/logos/4F3670ED-DDD7-4C6B-8EFF-5F8F00E95E56.PNG',
  '/logos/619651B8-9D6F-4882-8FF4-6C4B74289E7D.PNG',
  '/logos/877182FE-841D-46CC-8990-26C9A9D54816.jpeg',
  '/logos/882D0DE1-E035-4E1C-BC83-F3E6C6AF4E6D.jpeg',
  '/logos/A7A6D43C-D2C3-4AD8-AE6F-9EBBC72B4C43.PNG',
  '/logos/AE338CEF-A1C9-4651-BF99-975EA3A6B230.png',
  '/logos/chrysler.png',
  '/logos/D2CE872F-7F29-426F-A623-B91335C9902A.PNG',
  '/logos/Dodge-Logo-2010-present.jpg',
  '/logos/F3D6ABE7-EE2F-4554-8DEC-284405506AAD.png',
  '/logos/FC942694-25B5-4C5C-812E-56604326CA24.PNG',
  '/logos/FD050BC4-975B-40EB-8CF3-923558DE20A7.PNG',
  '/logos/jeep.png',
  '/logos/RAM.jpeg'
];

const partnerLogosNew = [
  '/partners/9.png',
  '/partners/JPEG image.jpeg',
  '/partners/pax_logo_colored.png',
  '/partners/PNG image 2.png',
  '/partners/PNG image 3.png',
  '/partners/PNG image.png',
  '/partners/pos.png',
  '/partners/pdi.png'
];

const Home: React.FC = () => {
  const { heroRowRef, heroContentRef, heroVisualRef } = useHeroEntrance();
  const productsStaggerRef = useStaggerReveal<HTMLDivElement>();
  const featuresStaggerRef = useStaggerReveal<HTMLDivElement>();
  const ctaStaggerRef = useStaggerReveal<HTMLDivElement>();
  const primaryCtaRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const btn = primaryCtaRef.current;
    if (!btn || prefersReducedMotion()) return;

    const arrow = btn.querySelector('svg');
    if (!arrow) return;

    const onEnter = () => {
      animate(arrow, { x: [0, 5], duration: 320, ease: 'outCubic' });
    };
    const onLeave = () => {
      animate(arrow, { x: [5, 0], duration: 280, ease: 'outCubic' });
    };

    btn.addEventListener('mouseenter', onEnter);
    btn.addEventListener('mouseleave', onLeave);
    return () => {
      btn.removeEventListener('mouseenter', onEnter);
      btn.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  const features = [
    {
      icon: <CheckCircle className="h-8 w-8 text-green-500" />,
      title: "Same-Day Deposits",
      description: "Get your money faster with same-day deposits, 7 days a week at no extra cost."
    },
    {
      icon: <Shield className="h-8 w-8 text-white" />,
      title: "Secure & Reliable",
      description: "Bank-level security with 99.9% uptime guarantee for uninterrupted business operations."
    },
    {
      icon: <Clock className="h-8 w-8 text-white" />,
      title: "24/7 Support",
      description: "Round-the-clock customer support to help you whenever you need assistance."
    }
  ];


  return (
    <div className="space-y-0">
      {/* Hero Section */}
      <section className="relative bg-gray-50 max-lg:pt-5 max-lg:pb-8 pt-8 sm:pt-8 md:pt-12 lg:pt-16 xl:pt-20 pb-3 sm:pb-8 md:pb-12 lg:pb-16 xl:pb-20 overflow-hidden min-h-0 max-lg:min-h-0 sm:min-h-screen flex items-center">
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div
            ref={heroRowRef}
            className="flex flex-col lg:flex-row gap-4 max-lg:gap-3 sm:gap-4 md:gap-6 lg:gap-8 items-center lg:items-center"
          >
            {/* Left Side - Content — first on phone */}
            <div
              ref={heroContentRef}
              className="w-full lg:w-1/2 flex flex-col justify-center max-lg:items-center max-lg:text-center lg:pl-4 xl:pl-8"
            >
              {/* Headline */}
              <h1
                data-hero="title"
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 max-lg:mb-2 sm:mb-4 md:mb-5 lg:mb-6 tracking-tight text-left max-lg:text-center text-gray-900 leading-tight"
              >
                Make your Payments{' '}
                <span data-hero="simpl" className="inline-block text-gray-900 origin-left">
                  Simpl.
                </span>
              </h1>
              {/* Description */}
              <p
                data-hero="subtitle"
                className="text-sm sm:text-base md:text-lg lg:text-xl mb-0 max-lg:mb-3 sm:mb-5 md:mb-6 lg:mb-8 text-gray-700 text-left max-lg:text-center leading-relaxed max-w-2xl max-lg:max-w-[20rem]"
              >
                Professional POS solutions with same-day deposits, competitive rates, and 24/7 support for your business.
              </p>
              {/* CTA Buttons — desktop / tablet landscape */}
              <div className="hidden lg:flex flex-col sm:flex-row gap-2.5 sm:gap-3 md:gap-4">
                <a
                  ref={primaryCtaRef}
                  data-hero="cta"
                  href="/contact"
                  className="bg-gray-900 text-white px-5 sm:px-6 md:px-8 lg:px-10 py-3 sm:py-3.5 md:py-4 lg:py-5 rounded-lg font-semibold text-sm sm:text-base md:text-lg hover:bg-gray-800 active:bg-gray-700 transition-colors inline-flex items-center justify-center gap-2 w-full sm:w-auto min-h-[48px] touch-manipulation"
                >
                  Get Started Today
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </a>
                <a
                  data-hero="cta"
                  href="/products"
                  className="border-2 border-gray-900 text-gray-900 px-5 sm:px-6 md:px-8 lg:px-10 py-3 sm:py-3.5 md:py-4 lg:py-5 rounded-lg font-semibold text-sm sm:text-base md:text-lg hover:bg-gray-900 hover:text-white active:bg-gray-800 transition-colors w-full sm:w-auto text-center min-h-[48px] touch-manipulation"
                >
                  View Products
                </a>
              </div>
            </div>

            {/* Right Side — product showcase + dealer logos */}
            <div
              ref={heroVisualRef}
              className="w-full lg:w-1/2 relative flex flex-col items-center max-lg:max-w-[min(100%,20rem)] max-lg:mx-auto mb-0 sm:mb-3 lg:mb-0 mt-0 max-lg:mt-1 sm:-mt-6 lg:-mt-4"
            >
              <div
                data-hero="processor-label"
                className="w-full max-w-[17.5rem] max-lg:max-w-[17.5rem] sm:max-w-md md:max-w-lg mb-1 max-lg:mb-1.5 sm:mb-2 md:mb-3 mx-auto text-center px-1"
              >
                <h3 className="text-sm max-lg:text-sm sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900">
                  Official Processor of
                </h3>
              </div>
              {/* Official-processor dealer logos — sits above the card so it never covers the animation */}
              <div className="w-full max-w-[17.5rem] max-lg:max-w-[17.5rem] sm:max-w-md md:max-w-lg mx-auto mb-2 sm:mb-3 rounded-lg sm:rounded-xl overflow-hidden bg-white/70 backdrop-blur-sm border border-gray-200/80 shadow-sm px-2 sm:px-3 md:px-4 py-1.5 sm:py-2.5 md:py-3">
                <div className="animate-marquee marquee-track">
                  {[0, 1].map((iteration) => (
                    <div key={iteration} className="marquee-track">
                      {partnerLogos.map((logo, index) => (
                        <div
                          key={`${logo}-${iteration}`}
                          className="marquee-item px-2 sm:px-4 md:px-5 lg:px-6 xl:px-8"
                        >
                          <img
                            src={logo}
                            alt={`Partner logo ${index + 1}`}
                            loading="lazy"
                            decoding="async"
                            className="h-5 sm:h-8 md:h-10 lg:h-11 w-auto max-w-[3.5rem] sm:max-w-none object-contain opacity-95 drop-shadow-sm transition-opacity duration-300 hover:opacity-100"
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div
                data-hero="hero-card"
                className="relative w-full max-w-[17.5rem] max-lg:max-w-[17.5rem] sm:max-w-md md:max-w-lg mx-auto aspect-square max-lg:max-h-[min(52vh,17.5rem)] max-lg:shadow-lg sm:aspect-square rounded-xl max-lg:rounded-2xl sm:rounded-xl md:rounded-2xl overflow-hidden shadow-xl ring-1 ring-gray-200/80"
              >
                <Suspense
                  fallback={
                    <div className="absolute inset-0 flex items-center justify-center bg-[#0a1120]">
                      <div className="h-9 w-9 animate-spin rounded-full border-2 border-slate-600 border-t-sky-400" />
                    </div>
                  }
                >
                  <HeroTransaction3D />
                </Suspense>
              </div>
            </div>

            {/* CTA Buttons — phone only, below showcase */}
            <div className="flex lg:hidden flex-col gap-2.5 w-full max-w-[17.5rem] mx-auto -mt-1">
              <a
                data-hero="cta"
                href="/contact"
                className="bg-gray-900 text-white px-5 py-3 rounded-lg font-semibold text-sm hover:bg-gray-800 active:bg-gray-700 transition-colors inline-flex items-center justify-center gap-2 w-full min-h-[48px] touch-manipulation"
              >
                Get Started Today
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                data-hero="cta"
                href="/products"
                className="border-2 border-gray-900 text-gray-900 px-5 py-3 rounded-lg font-semibold text-sm hover:bg-gray-900 hover:text-white active:bg-gray-800 transition-colors w-full text-center min-h-[48px] touch-manipulation"
              >
                View Products
              </a>
            </div>
          </div>
        </div>
      </section>

      <HomeAdOffer />

      {/* Who We Are */}
      <section className="bg-gray-900 overflow-hidden py-6 sm:py-8 md:py-12 lg:py-16">
        <ScrollReveal>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Partners Section - shown first at the top */}
            <div className="mb-6 sm:mb-8 lg:mb-10">
            <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 text-center">Official Partners of</h3>
            <div className="relative overflow-hidden bg-white/10 backdrop-blur-sm rounded-xl py-5 sm:py-6 md:py-7 lg:py-8 px-3 sm:px-4 border border-white/20 max-w-4xl mx-auto">
              <div className="animate-marquee-partners marquee-track">
                {[0, 1].map((iteration) => (
                  <div key={iteration} className="marquee-track flex items-center">
                    {partnerLogosNew.map((logo, index) => (
                      <div
                        key={`${logo}-${iteration}`}
                        className="marquee-item px-3 sm:px-4 md:px-5 flex-shrink-0 flex items-center justify-center"
                      >
                        <img
                          src={logo}
                          alt={`Partner logo ${index + 1}`}
                          loading="lazy"
                          decoding="async"
                          className="h-10 sm:h-12 md:h-16 lg:h-20 w-auto max-w-[100px] sm:max-w-[120px] md:max-w-[160px] object-contain opacity-90 hover:opacity-100 transition-opacity duration-300"
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row">
          {/* Mobile: Text content first, then image */}
          {/* Desktop: Image on left, text on right */}
          
          {/* Image on the left - bigger (60% width) - hidden on mobile */}
          <div className="hidden lg:flex w-full lg:w-3/5 flex-shrink-0 flex items-center justify-center">
            <img
              src="/who-we-are-bg.png"
              alt="Simpl Processing POS Terminal"
              className="max-w-full max-h-full h-[280px] sm:h-[350px] md:h-[400px] lg:h-[450px] object-contain object-center mx-auto"
              loading="eager"
              fetchPriority="high"
            />
          </div>
          
          {/* Description on the right (40% width) - shifted left */}
          <div className="w-full lg:w-2/5 text-white px-4 sm:px-6 md:px-8 lg:pl-10 lg:pr-0 py-4 sm:py-6 md:py-8 lg:py-12 flex flex-col justify-center lg:-ml-8">
            {/* Mobile: Show heading and description */}
            <div className="lg:hidden mb-4 sm:mb-5">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-center lg:text-left">Who We Are</h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-4 sm:mb-5 text-center lg:text-left leading-relaxed">
                Simpl Processing is a leading provider of payment processing solutions, helping businesses 
                of all sizes streamline their transactions and grow their revenue.
              </p>
            </div>

            {/* Mobile: Image in the middle */}
            <div className="lg:hidden mb-5 sm:mb-6 flex items-center justify-center">
              <img
                src="/who-we-are-bg.png"
                alt="Simpl Processing POS Terminal"
                className="max-w-full h-[220px] sm:h-[260px] md:h-[300px] object-contain object-center mx-auto"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>

            {/* Desktop: Show heading and description */}
            <div className="hidden lg:block">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">Who We Are</h2>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300">
                Simpl Processing is a leading provider of payment processing solutions, helping businesses 
                of all sizes streamline their transactions and grow their revenue.
              </p>
            </div>
          </div>
        </div>
        </div>
        </ScrollReveal>
      </section>

      {/* What We Sell */}
      <section className="py-6 sm:py-8 bg-gray-900 text-white">
        <ScrollReveal>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-4 sm:mb-6">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 px-4">What We Sell</h2>
            <p className="text-lg sm:text-xl text-gray-200 max-w-3xl mx-auto px-4">
              Complete payment processing solutions tailored to your business needs
            </p>
          </div>

          <div ref={productsStaggerRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div data-stagger-item className="bg-white/10 backdrop-blur-sm rounded-xl p-5 sm:p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 h-full flex flex-col">
              <div className="text-center flex flex-col flex-grow">
                <div className="bg-white/20 w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">POS Systems</h3>
                <p className="text-sm sm:text-base text-gray-200 mb-4 flex-grow">
                  Complete point-of-sale solutions including hardware, software, and payment processing
                </p>
                <a href="/products" className="text-white font-semibold hover:text-gray-300 flex items-center justify-center gap-2 text-sm sm:text-base mt-auto">
                  View Products →
                </a>
              </div>
            </div>

            <div data-stagger-item className="bg-white/10 backdrop-blur-sm rounded-xl p-5 sm:p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 h-full flex flex-col">
              <div className="text-center flex flex-col flex-grow">
                <div className="bg-white/20 w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">Online Payments</h3>
                <p className="text-sm sm:text-base text-gray-200 mb-4 flex-grow">
                  Secure online payment processing for e-commerce and digital transactions
                </p>
                <a href="/products" className="text-white font-semibold hover:text-gray-300 flex items-center justify-center gap-2 text-sm sm:text-base mt-auto">
                  Learn More →
                </a>
              </div>
            </div>

            <div data-stagger-item className="bg-white/10 backdrop-blur-sm rounded-xl p-5 sm:p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 sm:col-span-2 lg:col-span-1 h-full flex flex-col">
              <div className="text-center flex flex-col flex-grow">
                <div className="bg-white/20 w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">Mobile Solutions</h3>
                <p className="text-sm sm:text-base text-gray-200 mb-4 flex-grow">
                  Mobile payment processing for on-the-go businesses and pop-up shops
                </p>
                <a href="/products" className="text-white font-semibold hover:text-gray-300 flex items-center justify-center gap-2 text-sm sm:text-base mt-auto">
                  Explore Options →
                </a>
              </div>
            </div>
          </div>
        </div>
        </ScrollReveal>
      </section>

      {/* Features */}
      <section className="py-6 sm:py-8 bg-gray-900 text-white">
        <ScrollReveal>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-4 sm:mb-6">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 px-4">Why Choose Us</h2>
              <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto px-4">
                We provide everything you need to succeed in today's competitive market
              </p>
            </div>

            <div ref={featuresStaggerRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  data-stagger-item
                  className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/20 hover:bg-white/20 transition-all duration-300"
                >
                <div className="flex justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm sm:text-base text-gray-300">{feature.description}</p>
              </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* Contact CTA */}
      <section className="py-6 sm:py-8 bg-gray-900 text-white">
        <ScrollReveal>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3 px-4">Ready to Get Started?</h2>
          <p className="text-lg sm:text-xl mb-4 sm:mb-6 text-gray-200 max-w-2xl mx-auto px-4">
            Join thousands of businesses already using Simpl Processing for their payment needs
          </p>
          <div ref={ctaStaggerRef} className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <a
              data-stagger-item
              href="/contact"
              className="bg-white text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2 shadow-lg w-full sm:w-auto"
            >
              Get Your Quote
              <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
            </a>
            <a
              data-stagger-item
              href="tel:+12018460591"
              className="border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-white hover:text-gray-900 transition-colors inline-flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <Phone className="h-4 w-4 sm:h-5 sm:w-5" />
              Call (201) 846-0591
            </a>
          </div>
        </div>
        </ScrollReveal>
      </section>

      {/* Contact Info */}
      <section className="py-6 sm:py-8 bg-gray-900 text-white">
        <ScrollReveal>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 text-center">
            <div className="flex flex-col items-center">
              <Phone className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mb-2" />
              <h3 className="font-semibold mb-1 text-sm sm:text-base">Call Us</h3>
              <p className="text-gray-400 text-sm sm:text-base">(201) 846-0591</p>
              <p className="text-xs sm:text-sm text-gray-500">Mon-Fri 9AM-6PM EST</p>
            </div>
            <div className="flex flex-col items-center">
              <Mail className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mb-2" />
              <h3 className="font-semibold mb-1 text-sm sm:text-base">Email Us</h3>
              <p className="text-gray-400 text-sm sm:text-base break-all">simran@simplprocessing.com</p>
              <p className="text-xs sm:text-sm text-gray-500">We'll respond within 24 hours</p>
            </div>
            <div className="flex flex-col items-center">
              <MapPin className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mb-2" />
              <h3 className="font-semibold mb-1 text-sm sm:text-base">Visit Us</h3>
              <p className="text-gray-400 text-sm sm:text-base">New York, NY</p>
              <p className="text-xs sm:text-sm text-gray-500">Schedule an appointment</p>
            </div>
          </div>
        </div>
        </ScrollReveal>
      </section>
    </div>
  );
};

export default Home;